/**
 * Every "your club has no players" message in the app, in one place, as
 * `myClub` i18n keys; the surfaces call `t()` on them at render.
 *
 * Two surfaces share this copy: the dialog (a blocking prompt fired when the
 * user asks for something that needs club players) and the inline empty state
 * (rendered where a list of club players would have been). Both come in two
 * flavours, because "your import went empty" and "you never imported" are
 * different problems for the user.
 */

export type ClubEmptyStateContext =
  | "players"
  | "evolution-players"
  | "my-players"
  | "sbc-fodder"
  | "active-squad";

/** Label on the dialog's confirm button. */
export const IMPORT_DIALOG_CTA_LABEL = "common:import" as const;

/** Label on the inline empty state's CTA. `Hook` uppercases it. */
export const IMPORT_CTA_LABEL = "emptyClub.startImport" as const;

const REIMPORT_DIALOG = {
  title: "emptyClub.dialog.reimport.title",
  body: "emptyClub.dialog.reimport.body",
} as const;

const FIRST_IMPORT_DIALOG = {
  title: "emptyClub.dialog.firstImport.title",
  body: "emptyClub.dialog.firstImport.body",
} as const;

export const emptyClubDialogCopy = (hasImportedBefore: boolean) =>
  hasImportedBefore ? REIMPORT_DIALOG : FIRST_IMPORT_DIALOG;

/** Same headline wherever a club that once had players comes back empty. */
const REIMPORT_TITLE = "emptyClub.state.reimportTitle" as const;

const EMPTY_STATE_BY_CONTEXT = {
  players: {
    firstImportTitle: "emptyClub.state.players.firstImportTitle",
    firstImportSubtitle: "emptyClub.state.players.firstImportSubtitle",
    reimportSubtitle: "emptyClub.state.players.reimportSubtitle",
  },
  "evolution-players": {
    firstImportTitle: "emptyClub.state.evolutionPlayers.firstImportTitle",
    firstImportSubtitle: "emptyClub.state.evolutionPlayers.firstImportSubtitle",
    reimportSubtitle: "emptyClub.state.evolutionPlayers.reimportSubtitle",
  },
  "my-players": {
    firstImportTitle: "emptyClub.state.myPlayers.firstImportTitle",
    firstImportSubtitle: "emptyClub.state.myPlayers.firstImportSubtitle",
    reimportSubtitle: "emptyClub.state.myPlayers.reimportSubtitle",
  },
  "sbc-fodder": {
    firstImportTitle: "emptyClub.state.sbcFodder.firstImportTitle",
    firstImportSubtitle: "emptyClub.state.sbcFodder.firstImportSubtitle",
    reimportSubtitle: "emptyClub.state.sbcFodder.reimportSubtitle",
  },
  // The club can be full and still have no active squad, so this one never
  // claims the club is empty.
  "active-squad": {
    firstImportTitle: "emptyClub.state.activeSquad.firstImportTitle",
    firstImportSubtitle: "emptyClub.state.activeSquad.firstImportSubtitle",
    reimportTitle: "emptyClub.state.activeSquad.reimportTitle",
    reimportSubtitle: "emptyClub.state.activeSquad.reimportSubtitle",
  },
} as const satisfies Record<
  ClubEmptyStateContext,
  {
    firstImportTitle: string;
    firstImportSubtitle: string;
    reimportSubtitle: string;
    /** Only where "Your club is empty" would be the wrong diagnosis. */
    reimportTitle?: string;
  }
>;

type EmptyStateCopy = (typeof EMPTY_STATE_BY_CONTEXT)[ClubEmptyStateContext];
type EmptyStateTitleKey =
  | EmptyStateCopy["firstImportTitle"]
  | (typeof EMPTY_STATE_BY_CONTEXT)["active-squad"]["reimportTitle"]
  | typeof REIMPORT_TITLE;
type EmptyStateSubtitleKey =
  | EmptyStateCopy["firstImportSubtitle"]
  | EmptyStateCopy["reimportSubtitle"];

export const clubEmptyStateCopy = (
  context: ClubEmptyStateContext,
  hasImportedBefore: boolean,
): { title: EmptyStateTitleKey; subtitle: EmptyStateSubtitleKey } => {
  const copy = EMPTY_STATE_BY_CONTEXT[context];
  return {
    title: hasImportedBefore
      ? "reimportTitle" in copy
        ? copy.reimportTitle
        : REIMPORT_TITLE
      : copy.firstImportTitle,
    subtitle: hasImportedBefore
      ? copy.reimportSubtitle
      : copy.firstImportSubtitle,
  };
};
